/**
 * Token-Storage + Refresh-Flow fuer das Admin-SPA (Issue #297).
 *
 * Spiegelt das frontend-tokenService-Pattern: Refresh-Token wird mit dem
 * Access-Token in localStorage persistiert. Bei 401 versucht der
 * dataProvider-httpClient den Access-Token erst zu refreshen, bevor er den
 * User ausloggt — sonst fliegt Walter nach 1h JWT-TTL automatisch raus,
 * obwohl die SSO-Session weiter laeuft.
 *
 * Bewusst minimal gehalten gegenueber dem frontend-tokenService — das Admin
 * hat keinen H5P-Editor-Iframe, daher kein TOKEN_UPDATED_EVENT noetig.
 */

export const STORAGE_KEYS = {
    TOKEN: 'token',
    REFRESH_TOKEN: 'refresh_token',
    CODE_VERIFIER: 'code_verifier',
    REDIRECT_AFTER_LOGIN: 'redirect_after_login',
    USER_LOGGED_OUT: 'user_logged_out',
    AUTO_LOGIN_IN_PROGRESS: 'auto_login_in_progress',
} as const;

class TokenService {
    /**
     * In-flight refresh-promise — verhindert, dass mehrere parallele 401s
     * jeweils einen eigenen Refresh-Call ausloesen. Refresh-Tokens werden
     * vom SSO bei jedem erfolgreichen Tausch rotiert; ein zweiter Versuch
     * mit dem alten Token wuerde 400 zurueckgeben.
     */
    private refreshPromise: Promise<boolean> | null = null;

    getToken(): string | null {
        return localStorage.getItem(STORAGE_KEYS.TOKEN);
    }

    setToken(token: string): void {
        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    }

    removeToken(): void {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
    }

    getRefreshToken(): string | null {
        return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    }

    setRefreshToken(refreshToken: string): void {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }

    removeRefreshToken(): void {
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }

    /**
     * Tauscht den Refresh-Token gegen einen neuen Access-Token. Bei
     * concurrent Aufrufen wird auf eine einzige in-flight Promise
     * dedupliziert. Bei 4xx vom SSO werden beide Tokens entfernt (Refresh-
     * Token war invalid/abgelaufen → harter Logout faellig).
     */
    refreshAccessToken(): Promise<boolean> {
        if (this.refreshPromise !== null) {
            return this.refreshPromise;
        }

        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            return Promise.resolve(false);
        }

        const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
        const clientId = process.env.REACT_APP_OAUTH_CLIENT_ID || 'swagger-client';

        this.refreshPromise = (async () => {
            try {
                const response = await fetch(`${ssoUrl}/token`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({
                        grant_type: 'refresh_token',
                        refresh_token: refreshToken,
                        client_id: clientId,
                    }),
                });
                if (!response.ok) {
                    this.removeToken();
                    this.removeRefreshToken();
                    return false;
                }
                const data = await response.json();
                if (!data.access_token) {
                    return false;
                }
                this.setToken(data.access_token);
                if (data.refresh_token) {
                    this.setRefreshToken(data.refresh_token);
                }
                return true;
            } catch {
                return false;
            } finally {
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }
}

export const tokenService = new TokenService();
export default tokenService;
